import { useEffect, useState } from "react";
import { AppLayout } from "../components/layout/AppLayout";
import { Card } from "../components/ui/Card";
import { ScoreRing } from "../components/ui/ScoreRing";
import { api } from "../lib/api";

type Match = {
  id: string;
  company_name: string;
  company_logo: string | null;
  team_fit_score: number;
  career_fit_score: number;
  match_reasons_json: string;
  status: string;
  created_at: string;
};

export default function MyMatches() {
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ items: Match[] }>("/api/teamfit/matches")
      .then((res) => setMatches(res.items))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load matches."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AppLayout title="Team Offers">
      {loading && <p className="text-xs text-slate-400">Loading team offers...</p>}
      {error && <Card className="border border-coral/30 bg-coral/5 text-xs text-coral">{error}</Card>}

      {!loading && matches.length === 0 && (
        <Card className="border border-dashed border-slate-200 text-center text-xs text-slate-400">
          No team offers received yet. Complete your Persona so companies can reach out to you.
        </Card>
      )}

      <div className="space-y-2">
        {matches.map((m) => {
          const reasons: string[] = JSON.parse(m.match_reasons_json || "[]");
          return (
            <Card key={m.id} className="border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gradient-to-br from-navy to-teal" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-navy">{m.company_name}</p>
                  <p className="text-[11px] text-slate-400">Career Fit {m.career_fit_score}%</p>
                </div>
                <ScoreRing value={m.team_fit_score} size={48} strokeWidth={5} />
              </div>
              {reasons.length > 0 && (
                <ul className="mt-2 space-y-0.5 pl-4 text-xs text-slate-500">
                  {reasons.map((r, i) => (
                    <li key={i} className="list-disc">
                      {r}
                    </li>
                  ))}
                </ul>
              )}
              <span className="mt-2 inline-block rounded-full bg-slate-50 px-2 py-0.5 text-[10px] text-slate-500">
                {m.status}
              </span>
            </Card>
          );
        })}
      </div>
    </AppLayout>
  );
}
